import { Button, Content } from 'rbx';
import * as React from 'react';
import { useMutation } from '@apollo/client';

import { getCurrentUser } from '../../api/authorization';
import IBasketModel, { IBasketUpdateModel } from '../../models/IBasketModel';
import { GlobalRole } from '../../models/IUserModel';
import omit from '../../utils/omit';
import IsAdminQuery from '../auth/IsAdminQuery';
import { BASKET_LINE_ITEMS_QUERY, BASKET_UPDATE_MUTATION } from './../../api/GraphQLQueries/Basket';
import { notification } from './../../utils/notification';
import BasketFiles from './BasketFiles';
import BasketEditForm from './forms/BasketEditForm';
import BasketArchivedMutation from './queries/BasketArchivedMutation';
import BasketDeleteMutation from './queries/BasketDeleteMutation';
import BasketStatusMutation from './queries/BasketStatusMutation';

interface IProps {
    basket: IBasketModel;
}

const BasketDetails: React.FC<IProps> = ({ basket }) => {
    const [localBasket, setLocalBasket] = React.useState<IBasketModel | undefined>(undefined);

    const getBasketDetails = (): IBasketModel => {
        return {
            ...basket,
            ...(localBasket || {}),
        };
    };

    const basketDetails = getBasketDetails();

    const [updateBasketMutation] = useMutation(BASKET_UPDATE_MUTATION, {
        onCompleted: () => {
            setLocalBasket(undefined);
            notification.success('Successfully updated basket');
        },
        onError: () => {
            notification.error('Basket update error');
        },
        refetchQueries: [
            { query: BASKET_LINE_ITEMS_QUERY, variables: { id: basketDetails.id } },
        ],
    });

    const convertToBasketUpdateModel = (basketModel: IBasketModel): IBasketUpdateModel => {
        let basketUpdate: IBasketUpdateModel = omit(basketModel, [
            'updatedBy',
            'createdBy',
            'createdAt',
            'updatedAt',
            'plant',
            'lineItems',
            'status',
            'archived',
        ]);

        if (getCurrentUser().globalRole !== GlobalRole.linde_admin) {
            basketUpdate = omit(basketUpdate, ['purchaseState']);
        }

        return basketUpdate;
    };

    const handleUpdateBasketData = (newBasket: IBasketModel) => {
        setLocalBasket(newBasket);
    };

    const submitBasket = () => {
        const basketToSave = { ...basket, ...(localBasket || {}) };
        const basketUpdate = convertToBasketUpdateModel(basketToSave);
        updateBasketMutation({ variables: { basket: basketUpdate } });
    };

    const renderArchivedMutation = () => {
        const { archived, id } = basket;
        return id && <BasketArchivedMutation basketId={id} basketArchived={archived} />;
    };

    const renderAdminBasketDeleteMutation = () => {
        return <BasketDeleteMutation basket={getBasketDetails()} />;
    };

    const renderUserBasketDeleteMutation = () => {
        const { status } = basket;
        if (status !== 'draft') {
            return <></>;
        }
        return <BasketDeleteMutation basket={getBasketDetails()} />;
    };

    if (!basketDetails) {
        return null;
    }

    const { status, id, purchaseState } = basket;

    return (
        <>
            <BasketEditForm
                basket={basketDetails}
                onUpdate={handleUpdateBasketData}
            />

            <BasketFiles basketId={basketDetails.id} />

            <Content>
                <Button.Group>
                    <Button
                        color="primary"
                        disabled={!localBasket}
                        onClick={() => {
                            if (localBasket) {
                                submitBasket();
                            }
                        }}
                    >
                        Save {status !== 'draft' ? 'basket' : 'draft'}
                    </Button>
                    {status && id && (
                        <BasketStatusMutation
                            basketId={id}
                            basketStatus={status}
                        />
                    )}
                    {status !== 'draft' && purchaseState !== 'notset' && (
                        <IsAdminQuery
                            renderAdmin={renderArchivedMutation}
                        />
                    )}
                    <IsAdminQuery
                        renderAdmin={renderAdminBasketDeleteMutation}
                        renderUser={renderUserBasketDeleteMutation}
                    />
                </Button.Group>
            </Content>
        </>
    );
};

export default BasketDetails;
